import { cn } from "@omni/utils/styles";
import { ReactElement } from "react";
import {
    Controller,
    FieldValues,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { Container } from "@/components/General/Grid/container.component";

import { FormValuesExploded } from "../formField.types";

type SelectableCardFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    value: string;
    children: (props: SelectableCardFieldChildProps) => ReactElement;
};

type SelectableCardFieldChildProps = {
    isActive: boolean;
};

const SelectableCardField = <T extends FieldValues>({
    name,
    value,
    children
}: SelectableCardFieldProps<T>) => {
    const { control, setValue } = useFormContext<T>();

    const handleOnChange = () => {
        setValue(name, value as PathValue<T, Path<T>>);
    };

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const isActive = field.value === value;

                return (
                    <Label
                        className={cn(
                            "tw:w-full tw:p-2 tw:shadow-md tw:rounded-sm",
                            "tw:hover:border-grey-light tw:hover:border tw:hover:shadow-lg tw:hover:cursor-pointer",
                            {
                                "tw:border-grey-light tw:border tw:shadow-lg tw:cursor-auto":
                                    isActive
                            }
                        )}
                    >
                        <Input
                            name={name}
                            type="radio"
                            value={value}
                            className={"tw:sr-only"}
                            onChange={handleOnChange}
                            checked={isActive}
                        />
                        <Container>{children({ isActive })}</Container>
                    </Label>
                );
            }}
        />
    );
};

export { SelectableCardField };
